// M20.1 P0 — recover from stale lazy chunks after a deploy.
//
// During a Cloudflare Pages deploy, a tab can hold an OLD index/main bundle and
// then try to import a lazy route chunk whose hashed filename is momentarily
// unavailable on the edge (mid-propagation). Vite emits a `vite:preloadError`
// event (and the import rejects with "Failed to fetch dynamically imported
// module"). We do a ONE-TIME reload: a fresh load fetches the new index.html
// with the correct chunk hashes, recovering automatically.
//
// Safety: guarded by a sessionStorage timestamp so we reload at most once per
// tab per short TTL — never a reload loop. A second failure inside the TTL is
// left to the existing error boundary. No new dependency, no analytics event,
// no service-worker changes.

const STORAGE_KEY = "lazecast:dynamic-import-reload:v1";
const RELOAD_TTL_MS = 60_000;

/** Messages that indicate a failed dynamic import / chunk preload. */
const DYNAMIC_IMPORT_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

function reloadedRecently(now: number): boolean {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { ts?: unknown };
    return typeof parsed.ts === "number" && now - parsed.ts < RELOAD_TTL_MS;
  } catch {
    return false;
  }
}

function markReloaded(now: number, reason: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: now, reason }));
  } catch {
    // sessionStorage unavailable (private mode) — we simply lose the loop guard;
    // a real chunk-gone case would still be bounded by the browser, and a normal
    // deploy recovers on the first reload anyway.
  }
}

/** Reload once per TTL; never loop. */
function reloadOnce(reason: string, reload: () => void): void {
  const now = Date.now();
  if (reloadedRecently(now)) {
    // Second failure inside the TTL → do NOT loop. Let the error boundary show.
    console.warn(
      `[lazecast] dynamic import failed again within ${RELOAD_TTL_MS}ms; not reloading (${reason}).`,
    );
    return;
  }
  markReloaded(now, reason);
  reload();
}

/**
 * Install the guard. Call as early as possible (before render). Returns a
 * cleanup function that removes the listeners (used by tests). `opts.reload`
 * overrides the reload action for tests; production uses window.location.reload.
 */
export function installDeployReloadGuard(opts: { reload?: () => void } = {}): () => void {
  if (typeof window === "undefined") return () => {};
  const reload = opts.reload ?? (() => window.location.reload());

  const onPreloadError = (event: Event): void => {
    // Stop Vite's default (which would surface the raw error); we self-heal.
    event.preventDefault();
    reloadOnce("vite:preloadError", reload);
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason as { message?: unknown } | string | undefined;
    const message =
      typeof reason === "string"
        ? reason
        : typeof reason?.message === "string"
          ? reason.message
          : "";
    if (DYNAMIC_IMPORT_RE.test(message)) {
      reloadOnce("unhandledrejection", reload);
    }
  };

  window.addEventListener("vite:preloadError", onPreloadError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}
